import { Controller } from '@hotwired/stimulus';

// The body of a wiki page (wiki/_prose, or wiki/_rich): the address bar follows the
// reader. The top quarter of whatever scrolls the page (the window, or a
// host's box with the page inside it) is "being read": after every scroll,
// the last heading above that line is the section, and its id goes into the
// url hash; back at the top the hash goes, leaving the bare path. replaceState
// and never pushState (Back is not a ladder of sections), with the
// history.state Turbo keeps passed through, so Turbo's own restore and anchor
// visits still find what they put there. It never scrolls and never sets
// scroll-behavior: nothing to switch off for reduced motion.
//
// Every scroll, not a heading crossing the line: PageDown, Space or a flick
// moves the page more than a quarter in one frame, and a heading carried over
// the line never touches it (an IntersectionObserver here sat on a stale
// section, or never set one). One listener on the document, capture, since a
// box's scroll does not bubble; a box that does not hold the page (a wide
// table, a rail) is not the reader moving.
//
// A url that arrives with a hash is left alone until the first scroll after
// it: that one can be the browser landing on it, and a short section under
// it would read as "the next one" and drop what the url named.
//
// It writes only on the url it connected on: a Turbo visit puts the next
// page's url in the bar before it swaps this page out, and a scroll in between
// (a wheel still gliding when a prefetched link lands) would carry this
// section onto that page.
// ponytail: a Turbo visit that redirects into a wiki page renders before its
// url is replaced, so that page would stop writing; nothing redirects into one
// (the .pdf link is no Turbo visit).
//
// A rich page is written by hand: an h2/h3 with no id is given one here (its
// words, accents folded, unique in the document; the author's ids are never
// touched), and a url naming a generated id is scrolled to now that it exists.
// No `#` link: a designed heading takes no stray mark.
//
// ponytail: a last section shorter than the screen never reaches the line,
// so scrolling there keeps the section above it; a link to it is still
// honoured. Add a bottom-of-page rule if that ever bites.
//
// jane/docs/PAGES.md § Anchors. Registered as "wiki-anchors" by registerJane.
export default class extends Controller {
  connect() {
    this.name();
    this.heads = [...this.element.querySelectorAll('h2[id], h3[id], h4[id]')];
    if (!this.heads.length) return;
    this.first = true;
    this.url = location.pathname + location.search;
    this.read =({ target }) => this.follow(target === document ? document.scrollingElement : target);
    document.addEventListener('scroll', this.read, { capture: true, passive: true });
  }

  name() {
    let made = null;
    this.element.querySelectorAll('h2:not([id]), h3:not([id])').forEach((head) => {
      const base = head.textContent.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase()
        .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'section';
      let id = base;
      for (let n = 2; document.getElementById(id); n++) id = `${base}-${n}`;
      head.id = id;
      if (location.hash === `#${id}`) made = head;
    });
    made?.scrollIntoView();
  }

  disconnect() {
    if (this.read) document.removeEventListener('scroll', this.read, { capture: true });
  }

  follow(box) {
    if (!box?.contains(this.element) || location.pathname + location.search !== this.url) return;
    if (this.first) {
      this.first = false;
      if (location.hash) return;
    }
    const line = (box === document.scrollingElement ? 0 : box.getBoundingClientRect().top) + box.clientHeight / 4;
    const head = box.scrollTop < 1 ? null : this.heads.findLast((h) => h.getBoundingClientRect().top <= line);
    const hash = head ? `#${head.id}` : '';
    if (location.hash !== hash) history.replaceState(history.state, '', location.pathname + location.search + hash);
  }
}
